import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, BarChart3, Layers, AlertCircle } from "lucide-react";
import sMonoSand from "@/assets/s_mono_sand.png";
import sMonoCharcoal from "@/assets/s_mono_charcoal.png";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const from = location.state?.from?.pathname || "/dashboard";
  if (!loading && user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to sign in. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const demo = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[hsl(var(--primary))] p-12 text-white lg:flex">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0, transparent 35%)" }} />
        <div className="relative flex items-center gap-4">
          <img src={sMonoSand} alt="Sumadhura monogram" className="h-20 w-auto drop-shadow-sm" data-testid="login-brand-logo" />
          <div className="leading-tight">
            <p className="font-heading text-2xl font-semibold tracking-[0.22em]">SUMADHURA</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.34em] text-white/60">Foundation of Happiness</p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/75">Marketing Spends Tracker</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="max-w-md text-3xl font-semibold leading-tight font-heading">Every marketing rupee, tracked end-to-end.</h1>
          <p className="mt-3 max-w-md text-white/80">Plan budgets, log spends, and watch live dashboards reconcile across verticals, projects and quarters — in real time.</p>
          <div className="mt-8 space-y-3 text-sm">
            <div className="flex items-center gap-3"><BarChart3 className="h-5 w-5 shrink-0 text-white/80" /> Every expense tracked in one dashboard — no more juggling multiple Google Sheets</div>
            <div className="flex items-center gap-3"><Layers className="h-5 w-5 shrink-0 text-white/80" /> Clear segmentation & classification of spends — zero ambiguity in the data</div>
            <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 shrink-0 text-white/80" /> Role-based access with a full audit trail & data-integrity checks</div>
          </div>
        </div>
        <p className="relative text-xs text-white/60">© Sumadhura · Marketing Department</p>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src={sMonoCharcoal} alt="Sumadhura monogram" className="h-10 w-auto dark:hidden" />
            <img src={sMonoSand} alt="Sumadhura monogram" className="hidden h-10 w-auto dark:block" />
            <div className="leading-tight">
              <p className="font-heading text-sm font-semibold tracking-[0.18em]">SUMADHURA</p>
              <p className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Marketing Spends Tracker</p>
            </div>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight font-heading">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Use your team credentials to continue.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-[rgba(185,28,28,0.25)] bg-[rgba(185,28,28,0.08)] px-3 py-2 text-sm text-[rgb(185,28,28)]" data-testid="login-error">
                <AlertCircle className="h-4 w-4" /> {error}
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-slate-600">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@sumadhura.com" className="mt-1" required data-testid="login-email-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1" required data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)]" data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 rounded-xl border bg-secondary/40 p-3">
            <p className="text-xs font-medium text-muted-foreground">Demo accounts — click to fill</p>
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="outline" size="sm" className="flex-1 text-xs" onClick={() => demo("admin@sumadhura.com", "admin123")} data-testid="demo-admin-button">Admin</Button>
              <Button type="button" variant="outline" size="sm" className="flex-1 text-xs" onClick={() => demo("member@sumadhura.com", "member123")} data-testid="demo-member-button">Member</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
